import { useCallback } from 'react';
import useSWR from 'swr';

import { useTasksEnabled } from '~/components/tasks/useTasksEnabled';
import {
  taskLogLabelsArchiveCreate,
  taskLogLabelsCreate,
  taskLogLabelsList,
  taskLogLabelsPartialUpdate,
  taskLogLabelsReorderCreate,
} from '~/data/generated/endpoints/task-log-labels/task-log-labels';
import type {
  PatchedTaskLogLabelUpdateRequest,
  TaskLogLabelCreateRequest,
  TaskLogLabelWithCount,
} from '~/data/generated/model';
import { TaskLogLabelsListResponse } from '~/data/generated/zod/task-log-labels/task-log-labels.zod';
import { TASK_LOG_LABELS_KEY, useRevalidateTasks } from '~/data/hooks/taskKeys';
import { parseApiResponse } from '~/data/parseApiResponse';

export function useTaskLogLabels() {
  const enabled = useTasksEnabled();
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? TASK_LOG_LABELS_KEY : null,
    async () =>
      parseApiResponse(TaskLogLabelsListResponse, (await taskLogLabelsList()).data, 'TaskLogLabel'),
  );
  return { labels: data ?? [], error, isLoading, mutate };
}

export function useTaskLogLabelActions() {
  const { mutate } = useTaskLogLabels();
  const revalidateTasks = useRevalidateTasks();

  const run = useCallback(
    async (write: () => Promise<{ data: TaskLogLabelWithCount[] }>) => {
      const { data } = await write();
      await mutate(data, { revalidate: false });
      await revalidateTasks();
      return data;
    },
    [mutate, revalidateTasks],
  );

  return {
    addLabel: useCallback(
      (body: TaskLogLabelCreateRequest) => run(() => taskLogLabelsCreate(body)),
      [run],
    ),
    updateLabel: useCallback(
      (id: string, body: PatchedTaskLogLabelUpdateRequest) =>
        run(() => taskLogLabelsPartialUpdate(id, body)),
      [run],
    ),
    reorderLabels: useCallback(
      (ids: string[]) => run(() => taskLogLabelsReorderCreate({ ids })),
      [run],
    ),
    archiveLabel: useCallback((id: string) => run(() => taskLogLabelsArchiveCreate(id)), [run]),
  };
}
